import React from "react";
import { FormattedMessage, type MessageDescriptor } from "react-intl";

import { createTeeth, ElasticTooth } from "~/common/prescription";
import { Label } from "~/components/ui/label";
import type { TPrescription } from "~/types/prescription";

/**
 * Tooth-selection grid: 34px pitch per tooth, a 1px `#a3a3a3` divider between the two
 * quadrants of a jaw and between the upper/lower jaw rows, number label (14px/20px, `#404040`) above a
 * 16×16 control (border `#d4d4d4`, 4px radius). Was a Bootstrap `row`/`col-xs-12`/`checkbox-list` grid.
 */
function TeethJaw({ quads, children }: { quads: number[][]; children: (tooth: number) => React.ReactNode }) {
  return (
    <div className="tw-flex tw-items-start">
      {quads.map((quad, i) => (
        <React.Fragment key={quad.join(",")}>
          {/* Keep 16px between each tooth group and divider. */}
          {i > 0 ? <div className="tw-mx-4 tw-w-px tw-self-stretch tw-bg-[#a3a3a3]" aria-hidden /> : null}
          <div className="tw-flex">{quad.map((tooth) => children(tooth))}</div>
        </React.Fragment>
      ))}
    </div>
  );
}

/** Two stacked 1px `#a3a3a3` dividers between the upper/lower jaw rows: 13px from the
    checkbox row above, 8px between the two divider lines, 13px down to the next row's tooth numbers. */
function JawDivider() {
  return (
    <>
      <div className="tw-mt-[13px] tw-h-px tw-w-full tw-bg-[#a3a3a3]" aria-hidden />
      <div className="tw-mt-2 tw-h-px tw-w-full tw-bg-[#a3a3a3]" aria-hidden />
    </>
  );
}

function ToothCheckboxControl({ defaultChecked }: { defaultChecked: boolean }) {
  return <input type="checkbox" disabled defaultChecked={defaultChecked} className="tw-size-3.5 tw-cursor-not-allowed tw-accent-[#171717]" />;
}

export function PatientCardTeethItems({ label, defaultValue }: { label: MessageDescriptor["id"]; defaultValue: number[] }) {
  const teeth = createTeeth();

  if (defaultValue.length == 0) {
    return null;
  }

  return (
    <div className="PatientTeethItems form-group">
      <Label id="teeth-item-list" readOnly>
        <FormattedMessage id={label} />
      </Label>

      <div className="tw-flex tw-w-fit tw-flex-col tw-items-stretch">
        <TeethJaw quads={teeth.slice(0, 2)}>{(tooth) => <PatientCardToothItem key={tooth} tooth={tooth} defaultChecked={defaultValue.includes(tooth)} />}</TeethJaw>
        <JawDivider />
        <div className="tw-mt-[13px]">
          <TeethJaw quads={teeth.slice(2, 4)}>{(tooth) => <PatientCardToothItem key={tooth} tooth={tooth} defaultChecked={defaultValue.includes(tooth)} />}</TeethJaw>
        </div>
      </div>
    </div>
  );
}

/** A single tooth's number label + checkbox — also used directly (occlusal-plane teeth) where callers lay
    out their own row rather than the full `PatientCardTeethItems` jaw grid. */
export function PatientCardToothItem({ tooth, defaultChecked }: { tooth: number; defaultChecked: boolean }) {
  return (
    <div className="tw-flex tw-w-[34px] tw-shrink-0 tw-flex-col tw-items-center tw-gap-1">
      <span className="tw-text-sm tw-font-normal tw-leading-5 tw-text-[#404040]">{tooth}</span>
      <ToothCheckboxControl defaultChecked={defaultChecked} />
    </div>
  );
}

export function PatientCardElasticsTeethItems({ label, prescription }: { label: MessageDescriptor["id"]; prescription: TPrescription }) {
  const teeth = createTeeth();
  const elastics_options = prescription.elastics_options;

  // Hook and button values share the checkbox treatment; the active tooth is the checked state.
  function renderInput(i: number) {
    const active = elastics_options[i] == ElasticTooth.BUTTON || elastics_options[i] == ElasticTooth.HOOK;
    return (
      <div key={i} className="tw-flex tw-w-[34px] tw-shrink-0 tw-flex-col tw-items-center tw-gap-1">
        <span className="tw-text-sm tw-font-normal tw-leading-5 tw-text-[#404040]">{i}</span>
        <ToothCheckboxControl defaultChecked={active} />
      </div>
    );
  }

  return (
    <div className="PatientTeethItems form-group">
      <Label id="teeth-item-list" readOnly>
        <FormattedMessage id={label} />
      </Label>

      <div className="tw-flex tw-w-fit tw-flex-col tw-items-stretch">
        <TeethJaw quads={teeth.slice(0, 2)}>{renderInput}</TeethJaw>
        <JawDivider />
        <div className="tw-mt-[13px]">
          <TeethJaw quads={teeth.slice(2, 4)}>{renderInput}</TeethJaw>
        </div>
      </div>
    </div>
  );
}
